import React, { Suspense, lazy } from "react";
import { Navigate } from "react-router";
import { RouterProvider, createBrowserRouter } from "react-router-dom";

import { InvitationView } from "ui/components/InvitationView";
import { LoginView } from "ui/components/LoginView";
import { MainView } from "ui/components/MainView";
import { PasswordResetView } from "ui/components/PasswordResetView";
import { PasswordSetView } from "ui/components/PasswordSetView";
import { RunAsView } from "ui/components/RunAsView";
import { SupportCodeView } from "ui/components/SupportCodeView";
import { ThirdPartyConnectionView } from "ui/components/ThirdPartyConnectionView";
import { useAuthContext } from "ui/context/auth";
import { LoadingView, NoPermissionsView } from "ui/features";
import { useHasPermission } from "ui/hooks";

import { routePaths } from "./routePaths";

const LazyUserManagerApp = lazy(() => import("ui/features/userManagerApp"));
const LazyUsersView = lazy(() => import("ui/features/users"));
const LazyUserDetailsView = lazy(() => import("ui/features/userDetails"));
const LazyRolesView = lazy(() => import("ui/features/roles"));
const LazyRoleDetailsView = lazy(() => import("ui/features/roleDetails"));
const LazyCreateRoleView = lazy(() => import("ui/features/createRole"));
const LazyAdminSettingsView = lazy(() => import("ui/features/AdminSettingsView"));

const ProtectedRoute = (props: { children: React.ReactNode }) => {
  const { isLoggedIn } = useAuthContext();
  return isLoggedIn ? <>{props.children}</> : <Navigate to={routePaths.LOGIN} replace />;
};

const PermissionProtectedRoute = (props: { children: React.ReactNode; permission: string }) => {
  const hasAccess = useHasPermission(props.permission);
  const { fetchingPermissions } = useAuthContext();

  if (fetchingPermissions) {
    return <LoadingView />;
  }
  return hasAccess ? <>{props.children}</> : <NoPermissionsView />;
};

const createAppRouter = () =>
  createBrowserRouter([
    { path: routePaths.LOGIN, element: <LoginView /> },
    { path: routePaths.RESET_PASSWORD, element: <PasswordResetView /> },
    { path: routePaths.SET_PASSWORD, element: <PasswordSetView /> },
    { path: routePaths.INVITATION, element: <InvitationView /> },
    {
      path: routePaths.PARTNERS,
      children: [{ path: "linkedin/onboarding", element: <ThirdPartyConnectionView /> }],
    },
    { path: routePaths.SUPPORT_CODE, element: <SupportCodeView /> },
    { path: routePaths.RUN_AS, element: <RunAsView /> },
    {
      path: routePaths.PRODUCTS_VIEW,
      element: (
        <ProtectedRoute>
          <MainView />
        </ProtectedRoute>
      ),
    },
    {
      path: routePaths.USER_MANAGER_APP.ROOT,
      element: (
        <ProtectedRoute>
          <PermissionProtectedRoute permission="auth:user">
            <Suspense fallback={<LoadingView />}>
              <LazyUserManagerApp />
            </Suspense>
          </PermissionProtectedRoute>
        </ProtectedRoute>
      ),
      children: [
        {
          path: routePaths.USER_MANAGER_APP.USERS_VIEW_RELATIVE,
          element: (
            <PermissionProtectedRoute permission="auth:user:read">
              <Suspense fallback={<LoadingView />}>
                <LazyUsersView />
              </Suspense>
            </PermissionProtectedRoute>
          ),
          children: [
            {
              path: ":id",
              element: (
                <Suspense fallback={<LoadingView />}>
                  <LazyUserDetailsView />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: routePaths.USER_MANAGER_APP.ROLES_VIEW_RELATIVE,
          element: (
            <PermissionProtectedRoute permission="auth:role:read">
              <Suspense fallback={<LoadingView />}>
                <LazyRolesView />
              </Suspense>
            </PermissionProtectedRoute>
          ),
          children: [
            {
              path: "create",
              element: (
                <Suspense fallback={<LoadingView />}>
                  <LazyCreateRoleView />
                </Suspense>
              ),
            },
            {
              path: ":id",
              element: (
                <Suspense fallback={<LoadingView />}>
                  <LazyRoleDetailsView />
                </Suspense>
              ),
            },
          ],
        },
        {
          path: routePaths.USER_MANAGER_APP.ADMIN_PANEL_RELATIVE,
          element: (
            <PermissionProtectedRoute permission="auth:superadmin">
              <Suspense fallback={<LoadingView />}>
                <LazyAdminSettingsView />
              </Suspense>
            </PermissionProtectedRoute>
          ),
        },
      ],
    },
    { path: "*", element: <Navigate to={routePaths.PRODUCTS_VIEW} replace /> },
  ]);

const AppRouting = () => {
  return <RouterProvider router={createAppRouter()} />;
};

export default AppRouting;
